// Shared interfaces for the Tibia Mobile game

// Direction type for movement and animations
export type Direction = 'up' | 'down' | 'left' | 'right'

// Combat statistics shared by players and creatures
export interface CombatStats {
  maxHp: number
  currentHp: number
  maxMp: number
  currentMp: number
  attack: number
  defense: number
  attackSpeed: number  // cooldown in ms
  attackRange: number  // distance in pixels
}

// Player-specific stats including progression
export interface PlayerStats extends CombatStats {
  level: number
  experience: number
  experienceToNextLevel: number
}

// AI States for creatures
export type AIState = 'idle' | 'wandering' | 'chasing' | 'attacking' | 'returning' | 'dead'

// Creature type identifiers
export type CreatureType = 'rat' | 'snake' | 'wolf'

// Definition for a creature type (used by factory)
export interface CreatureDefinition {
  type: CreatureType
  name: string
  maxHp: number
  attack: number
  defense: number
  experienceReward: number
  speed: number          // pixels per second
  aggroRange: number     // pixels
  attackRange: number    // pixels
  attackSpeed: number    // cooldown in ms
  respawnTime: number    // ms
  color: string          // main body color
  secondaryColor: string // detail color
}

// Spawn zone configuration
export interface SpawnZone {
  x: number       // tile x
  y: number       // tile y
  width: number   // tiles wide
  height: number  // tiles tall
  creatureTypes: CreatureType[]
  maxCreatures: number
  currentCreatures: number
}

// Combat event data
export interface DamageEvent {
  attacker: 'player' | CreatureType
  target: 'player' | CreatureType
  damage: number
  isCritical: boolean
  targetX: number
  targetY: number
}

// Level up event data
export interface LevelUpEvent {
  newLevel: number
  hpGain: number
  mpGain: number
  attackGain: number
  defenseGain: number
}

// Death event data
export interface DeathEvent {
  entity: 'player' | CreatureType
  killedBy: 'player' | CreatureType | null
  x: number
  y: number
}

// Custom event types
export const EVENTS = {
  DAMAGE_DEALT: 'damage-dealt',
  CREATURE_DIED: 'creature-died',
  PLAYER_DIED: 'player-died',
  PLAYER_RESPAWNED: 'player-respawned',
  LEVEL_UP: 'level-up',
  EXPERIENCE_GAINED: 'experience-gained',
  TARGET_CHANGED: 'target-changed',
  STATS_CHANGED: 'stats-changed'
} as const

// Tile indices (exported for spawn system)
export const TILES = {
  GRASS1: 0, GRASS2: 1, GRASS3: 2, GRASS_FLOWER: 3,
  WATER1: 4, WATER2: 5, WATER_EDGE: 6, WATER_DEEP: 7,
  SAND1: 8, SAND2: 9, DIRT1: 10, DIRT2: 11,
  STONE1: 12, STONE2: 13, STONE_PATH: 14, COBBLE: 15,
  TREE1: 16, TREE2: 17, BUSH: 18, ROCK: 19,
  WALL_H: 20, WALL_V: 21, ROOF: 22, FLOOR: 23,
  DOOR: 24, WINDOW: 25, FENCE: 26, BARREL: 27,
  CRATE: 28, WELL: 29, SIGNPOST: 30, LAMP: 31
} as const

// Spawn configuration constants
export const SPAWN_CONFIG = {
  ZONE_SIZE: 10,          // tiles per zone
  MAX_PER_ZONE: 3,        // max creatures per zone
  TOWN_MIN_X: 22,         // town area to avoid
  TOWN_MAX_X: 34,
  TOWN_MIN_Y: 22,
  TOWN_MAX_Y: 34,
  RESPAWN_TIME: 30000     // 30 seconds
} as const

// Player base stats
export const PLAYER_BASE_STATS = {
  maxHp: 100,
  maxMp: 50,
  attack: 10,
  defense: 3,
  attackSpeed: 600,      // 600ms cooldown (faster attacks)
  attackRange: 50        // 50px range (close combat)
} as const

// Level up bonuses
export const LEVEL_UP_BONUSES = {
  hp: 15,
  mp: 10,
  attack: 2,
  defense: 1
} as const

// Spell types
export type SpellType = 'attack' | 'heal' | 'fireBolt'

// Spell definition
export interface SpellDefinition {
  type: SpellType
  name: string
  manaCost: number
  cooldown: number        // ms
  range: number           // pixels (0 for self-target)
  minDamage?: number      // for damage spells
  maxDamage?: number      // for damage spells
  healAmount?: number     // for healing spells
  icon: string            // texture key
}

// Spell definitions
export const SPELLS: Record<SpellType, SpellDefinition> = {
  attack: {
    type: 'attack',
    name: 'Attack',
    manaCost: 0,
    cooldown: 600,        // same as player attack speed
    range: 50,            // melee range (matches PLAYER_BASE_STATS)
    minDamage: 0,         // uses player attack stat
    maxDamage: 0,
    icon: 'icon-attack'
  },
  heal: {
    type: 'heal',
    name: 'Heal',
    manaCost: 15,
    cooldown: 3000,       // 3 seconds
    range: 0,             // self-target
    healAmount: 25,
    icon: 'icon-heal'
  },
  fireBolt: {
    type: 'fireBolt',
    name: 'Fire Bolt',
    manaCost: 10,
    cooldown: 1500,       // 1.5 seconds
    range: 150,           // ranged spell
    minDamage: 20,
    maxDamage: 30,
    icon: 'icon-fire'
  }
} as const

// Item types
export type ItemType = 'potion' | 'gold' | 'equipment'

// Item definition
export interface ItemDefinition {
  id: string
  name: string
  type: ItemType
  value: number              // gold value
  effect?: {
    hp?: number              // heal HP amount
    mp?: number              // restore MP amount
  }
  stackable: boolean
  maxStack: number
}

// Loot drop configuration
export interface LootDrop {
  itemId: string
  chance: number            // 0-1 probability
  minQty: number
  maxQty: number
}

// Loot table per creature
export type LootTable = LootDrop[]

// Item definitions
export const ITEMS: Record<string, ItemDefinition> = {
  gold: {
    id: 'gold',
    name: 'Gold',
    type: 'gold',
    value: 1,
    stackable: true,
    maxStack: 99999
  },
  healthPotion: {
    id: 'healthPotion',
    name: 'Health Potion',
    type: 'potion',
    value: 50,
    effect: { hp: 50 },
    stackable: true,
    maxStack: 100
  },
  smallHealthPotion: {
    id: 'smallHealthPotion',
    name: 'Small Health Potion',
    type: 'potion',
    value: 25,
    effect: { hp: 25 },
    stackable: true,
    maxStack: 100
  },
  manaPotion: {
    id: 'manaPotion',
    name: 'Mana Potion',
    type: 'potion',
    value: 40,
    effect: { mp: 30 },
    stackable: true,
    maxStack: 100
  }
} as const

// Loot tables per creature type
export const LOOT_TABLES: Record<CreatureType, LootTable> = {
  rat: [
    { itemId: 'gold', chance: 1.0, minQty: 1, maxQty: 5 },
    { itemId: 'smallHealthPotion', chance: 0.10, minQty: 1, maxQty: 1 }
  ],
  snake: [
    { itemId: 'gold', chance: 1.0, minQty: 3, maxQty: 10 },
    { itemId: 'healthPotion', chance: 0.15, minQty: 1, maxQty: 1 },
    { itemId: 'manaPotion', chance: 0.10, minQty: 1, maxQty: 1 }
  ],
  wolf: [
    { itemId: 'gold', chance: 1.0, minQty: 8, maxQty: 20 },
    { itemId: 'healthPotion', chance: 0.25, minQty: 1, maxQty: 1 },
    { itemId: 'manaPotion', chance: 0.20, minQty: 1, maxQty: 1 }
  ]
} as const

// Inventory item (with quantity for stackable items)
export interface InventoryItem {
  itemId: string
  quantity: number
}

// Inventory events
export const INVENTORY_EVENTS = {
  ITEM_ADDED: 'inventory-item-added',
  ITEM_REMOVED: 'inventory-item-removed',
  ITEM_USED: 'inventory-item-used',
  GOLD_CHANGED: 'inventory-gold-changed',
  INVENTORY_CHANGED: 'inventory-changed'
} as const

// Loot events
export const LOOT_EVENTS = {
  LOOT_DROPPED: 'loot-dropped',
  LOOT_COLLECTED: 'loot-collected'
} as const
